import { useFormInput } from "#src/commonV2/form/Input/useFormInput";
import { VatTypeValue } from "#src/services/payment/enum";
import { IWorkspacePaymentSettings } from "#src/services/payment/types";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { IVatInvoiceRenderProps } from "./types";

export enum VatInvoiceField {
  INVOICE_TYPE = "invoiceType",
  COMPANY_NAME = "companyName",
  TAX_CODE = "taxCode",
  COMPANY_ADDRESS = "companyAddress",
  FULL_NAME = "fullName",
  CITIZEN_ID = "citizenId",
  PERSONAL_ADDRESS = "personalAddress",
}

/** This hook is written to organize and split code into smaller chunks.
 * Reusability is not an intended feature, but if we need, and we can, that's good. */
export const useVatInvoiceForm = (
  defaultPayment: IWorkspacePaymentSettings | null
) => {
  const { t } = useTranslation("common", {
    keyPrefix: "v2.payment.enterInformation.vatInvoice",
  });

  // --------------- STATES ---------------

  const [invoiceType, setInvoiceType] = useState<VatTypeValue>(
    VatTypeValue.COMPANY
  );

  // --------------- HOOKS ---------------

  // For company invoice
  const {
    value: companyName,
    reset: resetCompanyNameField,
    wasChanged: companyNameWasChanged,
    setExternalError: setCompanyNameExternalError,
    hasError: companyNameHasError,
    renderProps: companyNameFieldRenderProps,
  } = useFormInput({
    label: t("companyName.label"),
    placeholder: t("companyName.placeholder"),
    defaultValue:
      defaultPayment?.vat_type === VatTypeValue.COMPANY
        ? (defaultPayment.vat_name ?? undefined)
        : undefined,
    validation: { required: invoiceType === VatTypeValue.COMPANY },
  });

  const {
    value: taxCode,
    reset: resetTaxCodeField,
    wasChanged: taxCodeWasChanged,
    setExternalError: setTaxCodeExternalError,
    hasError: taxCodeHasError,
    renderProps: taxCodeFieldRenderProps,
  } = useFormInput({
    label: t("taxCode.label"),
    placeholder: t("taxCode.placeholder"),
    defaultValue: defaultPayment?.vat_tax_code ?? undefined,
    validation: { required: invoiceType === VatTypeValue.COMPANY },
  });

  const {
    value: companyAddress,
    reset: resetCompanyAddressField,
    wasChanged: companyAddressWasChanged,
    setExternalError: setCompanyAddressExternalError,
    hasError: companyAddressHasError,
    renderProps: companyAddressFieldRenderProps,
  } = useFormInput({
    label: t("address.label"),
    placeholder: t("address.placeholder"),
    defaultValue:
      defaultPayment?.vat_type === VatTypeValue.COMPANY
        ? (defaultPayment.vat_address ?? undefined)
        : undefined,
    validation: { required: invoiceType === VatTypeValue.COMPANY },
  });

  // For personal invoice
  const {
    value: fullName,
    reset: resetFullNameField,
    wasChanged: fullNameWasChanged,
    setExternalError: setFullNameExternalError,
    hasError: fullNameHasError,
    renderProps: fullNameFieldRenderProps,
  } = useFormInput({
    label: t("fullName.label"),
    placeholder: t("fullName.placeholder"),
    defaultValue:
      defaultPayment?.vat_type === VatTypeValue.PERSONAL
        ? (defaultPayment.vat_name ?? undefined)
        : undefined,
    validation: { required: invoiceType === VatTypeValue.PERSONAL },
  });

  const {
    value: citizenId,
    reset: resetCitizenIdField,
    wasChanged: citizenIdWasChanged,
    setExternalError: setCitizenIdExternalError,
    hasError: citizenIdHasError,
    renderProps: citizenIdFieldRenderProps,
  } = useFormInput({
    label: t("citizenId.label"),
    placeholder: t("citizenId.placeholder"),
    defaultValue: defaultPayment?.vat_identity_card ?? undefined,
    validation: { required: invoiceType === VatTypeValue.PERSONAL },
  });

  const {
    value: personalAddress,
    reset: resetPersonalAddressField,
    wasChanged: personalAddressWasChanged,
    setExternalError: setPersonalAddressExternalError,
    hasError: personalAddressHasError,
    renderProps: personalAddressFieldRenderProps,
  } = useFormInput({
    label: t("address.label"),
    placeholder: t("address.placeholder"),
    defaultValue:
      defaultPayment?.vat_type === VatTypeValue.PERSONAL
        ? (defaultPayment.vat_address ?? undefined)
        : undefined,
    validation: { required: invoiceType === VatTypeValue.PERSONAL },
  });

  // --------------- METHODS ---------------

  const resetForm = useCallback(() => {
    resetFullNameField();
    resetCompanyNameField();
    resetTaxCodeField();
    resetCitizenIdField();
    resetPersonalAddressField();
    resetCompanyAddressField();
  }, [
    resetFullNameField,
    resetCompanyNameField,
    resetTaxCodeField,
    resetCitizenIdField,
    resetPersonalAddressField,
    resetCompanyAddressField,
  ]);

  const onChangeInvoiceType = useCallback(
    (value: VatTypeValue) => {
      setInvoiceType(value);
      resetForm();
    },
    [resetForm]
  );

  // --------------- COMPUTED ---------------

  const renderProps = useMemo(
    () =>
      ({
        onChangeInvoiceType,
        companyName: companyNameFieldRenderProps,
        taxCode: taxCodeFieldRenderProps,
        companyAddress: companyAddressFieldRenderProps,
        fullName: fullNameFieldRenderProps,
        citizenId: citizenIdFieldRenderProps,
        personalAddress: personalAddressFieldRenderProps,
      }) satisfies IVatInvoiceRenderProps,
    [
      onChangeInvoiceType,
      companyNameFieldRenderProps,
      taxCodeFieldRenderProps,
      companyAddressFieldRenderProps,
      fullNameFieldRenderProps,
      citizenIdFieldRenderProps,
      personalAddressFieldRenderProps,
    ]
  );

  const value = useMemo(
    () =>
      ({
        invoiceType,
        companyName,
        taxCode,
        companyAddress,
        fullName,
        citizenId,
        personalAddress,
      }) satisfies {
        invoiceType: VatTypeValue;
        companyName: string;
        taxCode: string;
        companyAddress: string;
        fullName: string;
        citizenId: string;
        personalAddress: string;
      },
    [
      invoiceType,
      companyName,
      taxCode,
      companyAddress,
      fullName,
      citizenId,
      personalAddress,
    ]
  );

  const setExternalError = useMemo(
    () =>
      ({
        companyName: setCompanyNameExternalError,
        taxCode: setTaxCodeExternalError,
        companyAddress: setCompanyAddressExternalError,
        fullName: setFullNameExternalError,
        citizenId: setCitizenIdExternalError,
        personalAddress: setPersonalAddressExternalError,
      }) satisfies {
        [F in Exclude<VatInvoiceField, VatInvoiceField.INVOICE_TYPE>]: Dispatch<
          SetStateAction<string>
        >;
      },
    [
      setCompanyNameExternalError,
      setTaxCodeExternalError,
      setCompanyAddressExternalError,
      setFullNameExternalError,
      setCitizenIdExternalError,
      setPersonalAddressExternalError,
    ]
  );

  const hasError: boolean = useMemo(
    () =>
      companyNameHasError ||
      companyAddressHasError ||
      taxCodeHasError ||
      fullNameHasError ||
      citizenIdHasError ||
      personalAddressHasError,
    [
      companyNameHasError,
      companyAddressHasError,
      taxCodeHasError,
      fullNameHasError,
      citizenIdHasError,
      personalAddressHasError,
    ]
  );

  const wasChanged: boolean = useMemo(
    () =>
      (defaultPayment &&
        defaultPayment.vat_type &&
        invoiceType !== defaultPayment?.vat_type) ||
      companyNameWasChanged ||
      companyAddressWasChanged ||
      taxCodeWasChanged ||
      fullNameWasChanged ||
      citizenIdWasChanged ||
      personalAddressWasChanged,
    [
      defaultPayment,
      invoiceType,
      companyNameWasChanged,
      companyAddressWasChanged,
      taxCodeWasChanged,
      fullNameWasChanged,
      citizenIdWasChanged,
      personalAddressWasChanged,
    ]
  );

  // --------------- EFFECTS ---------------

  useEffect(() => {
    if (defaultPayment?.vat_type) setInvoiceType(defaultPayment.vat_type);
  }, [defaultPayment?.vat_type]);

  // --------------- RETURN ---------------

  return {
    value,
    setExternalError,
    hasError,
    wasChanged,
    resetForm,
    renderProps,
  };
};
